import { yupResolver } from '@hookform/resolvers/yup'
import { FC } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'

import { CreateMemberPayload, EPermissions, Member } from 'types/organization'

import { snakeToLabel } from 'utils/snakeToLabel'

import {
  useCreateOrganizationMemberMutation,
  useUpdateOrganizationMemberMutation,
} from 'services/organizations'

import Modal from 'components/Modal'
import { TModalVariant } from 'components/Modal/types'
import MultiSelectAutocomplete from 'components/MultiSelectAutocomplete'
import { createOrganizationMemberSchema } from 'components/OrganizationMemberActionModal/validation'
import TextInput from 'components/TextInput'

interface Props {
  variant: TModalVariant
  onClose: () => void
  member?: Member
  organizationId?: string
}

const permissionOptions = Object.values(EPermissions).map((perm) => ({
  value: perm,
  label: snakeToLabel(perm),
}))

type CreateMemberPayloadWithoutOrganizationId = Omit<CreateMemberPayload, 'organizationId'>

const OrganizationMemberActionModal: FC<Props> = ({ variant, onClose, member, organizationId }) => {
  const { t } = useTranslation()
  const isEditModal = variant === 'edit'

  const [create, { isLoading: isCreateLoading }] = useCreateOrganizationMemberMutation()
  const [update, { isLoading: isUpdateLoading }] = useUpdateOrganizationMemberMutation()

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreateMemberPayloadWithoutOrganizationId>({
    mode: 'onBlur',
    resolver: yupResolver(createOrganizationMemberSchema),
    defaultValues: member
      ? {
          username: member.memberUsername,
          permissions: member.permissions,
        }
      : undefined,
  })

  const onSubmit = (formData: CreateMemberPayloadWithoutOrganizationId) => {
    console.log({ organizationId, ...formData })
    if (isEditModal && member) {
      update({ memberId: member.memberId, permissions: formData.permissions })
        .unwrap()
        .then(onClose)
        .catch(() => null)
    } else if (organizationId) {
      create({ organizationId, ...formData })
        .unwrap()
        .then(onClose)
        .catch(() => null)
    }
  }

  const title =
    variant === 'create'
      ? t('Pages.Organizations.AddNewMember')
      : t('Pages.Organizations.EditPermissions')

  return (
    <Modal open handleClose={onClose} title={title} modalWidth={500}>
      <Box
        display="flex"
        gap={3}
        flexDirection="column"
        component={'form'}
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextInput
          name="username"
          label={t('Pages.Organizations.Username')}
          control={control}
          errors={errors}
          disabled={isEditModal}
        />
        {!isEditModal && (
          <TextInput
            name="email"
            label={t('Pages.Organizations.Email')}
            control={control}
            errors={errors}
          />
        )}
        <MultiSelectAutocomplete
          name="permissions"
          label={t('Pages.Organizations.SelectPermissions')}
          control={control}
          errors={errors}
          options={permissionOptions}
        />

        <Box display="grid" gridTemplateColumns={'1fr 1fr'} gap={{ xs: 2, sm: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            {t('Buttons.Cancel')}
          </Button>
          <Button variant="contained" type="submit" disabled={isCreateLoading || isUpdateLoading}>
            {t('Buttons.Save')}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default OrganizationMemberActionModal
